import { useState, useMemo } from "react";
import {
  ColumnDef,
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import dayjs from "dayjs";
import MainTable from "@/components/main-table";
import TablePagination from "@/components/table-pagination";
import TableColumnsManager from "@/components/table-columns-manager";
import SearchInput from "@/components/search-input";
import { cn } from "@/utils/classnames";
import { CustomerPayment } from "@/queries/customer-payments/types";
import { useInvoiceDrawer } from "@/hooks/invoice/use-invoice-drawer";
import { useQuickPayViewDrawer } from "@/hooks/invoice/use-quickpay-view-drawer";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button-tw";
import { ChevronDown } from "lucide-react";

const StatusBadge = ({ status }: { status: string }) => {
  const normalized = status?.toLowerCase();
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset",
        normalized === "paid" || normalized === "completed"
          ? "bg-green-50 text-green-700 ring-green-600/20"
          : normalized === "pending" || normalized === "processing"
          ? "bg-yellow-50 text-yellow-700 ring-yellow-600/20"
          : normalized === "failed" || normalized === "unpaid"
          ? "bg-red-50 text-red-700 ring-red-600/20"
          : "bg-gray-50 text-gray-600 ring-gray-500/20",
      )}
    >
      {status}
    </span>
  );
};

const columns: ColumnDef<CustomerPayment>[] = [
  {
    id: "date",
    header: "Date",
    accessorKey: "create_date",
    cell: ({ getValue }) => dayjs(getValue<string>()).format("MM/DD/YYYY"),
  },
  {
    id: "paymentId",
    header: "Payment ID",
    accessorKey: "payment_id",
  },
  {
    id: "from",
    header: "From",
    accessorKey: "customer_name",
  },
  {
    id: "source",
    header: "Source",
    accessorKey: "description",
    cell: ({ getValue }) => {
      const val = getValue<string>() ?? "";
      if (val.startsWith("INV")) return `INV ${val.slice(3)}`;
      if (val.startsWith("QP")) return `QP ${val.slice(2)}`;
      return val;
    },
  },
  {
    id: "paymentType",
    header: "Payment Type",
    accessorKey: "payment_type",
  },
  {
    id: "amount",
    header: "Amount",
    accessorKey: "payment_amount",
    cell: ({ getValue }) => {
      const val = getValue<number>();
      return `$${Number(val).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
  },
  {
    id: "status",
    header: "Status",
    accessorKey: "payment_status",
    cell: ({ getValue }) => <StatusBadge status={getValue<string>()} />,
  },
  {
    id: "appliedTo",
    header: "Applied To",
    accessorKey: "applied_to",
  },
  {
    id: "tranId",
    header: "Batch ID",
    accessorKey: "batch_id",
  },
];

interface CustomerPaymentsTableProps {
  data: CustomerPayment[];
  isLoading: boolean;
}

const FilterDropdown = ({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: string[];
  selected: string[];
  onChange: (val: string) => void;
}) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="outline" className="gap-1.5">
        {label}
        {selected.length > 0 && (
          <span className="ml-1 rounded bg-gray-900 px-1.5 py-0.5 text-xs text-white">
            {selected.length}
          </span>
        )}
        <ChevronDown size={14} className="opacity-60" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start">
      <DropdownMenuLabel>{label}</DropdownMenuLabel>
      {options.map((opt) => (
        <DropdownMenuCheckboxItem
          key={opt}
          checked={selected.includes(opt)}
          onCheckedChange={() => onChange(opt)}
          onSelect={(e) => e.preventDefault()}
        >
          {opt}
        </DropdownMenuCheckboxItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

export const CustomerPaymentsTable = ({
  data,
  isLoading,
}: CustomerPaymentsTableProps) => {
  const { handleOpen: openInvoice } = useInvoiceDrawer();
  const { handleOpen: openQuickPay } = useQuickPayViewDrawer();

  const [sorting, setSorting] = useState<SortingState>([
    { id: "date", desc: true },
  ]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<string[]>([]);

  const statusOptions = useMemo(
    () =>
      [...new Set(data.map((r) => r.payment_status).filter(Boolean))].sort(),
    [data],
  );
  const typeOptions = useMemo(
    () =>
      [
        ...new Set(
          data.map((r) => r.payment_type).filter((t) => t && t !== "N/A"),
        ),
      ].sort(),
    [data],
  );

  const filteredData = useMemo(() => {
    let result = data;
    if (statusFilter.length > 0) {
      result = result.filter((r) => statusFilter.includes(r.payment_status));
    }
    if (typeFilter.length > 0) {
      result = result.filter((r) => typeFilter.includes(r.payment_type));
    }
    return result;
  }, [data, statusFilter, typeFilter]);

  const toggleStatus = (val: string) =>
    setStatusFilter((prev) =>
      prev.includes(val) ? prev.filter((s) => s !== val) : [...prev, val],
    );

  const toggleType = (val: string) =>
    setTypeFilter((prev) =>
      prev.includes(val) ? prev.filter((t) => t !== val) : [...prev, val],
    );

  const handleRowClick = (row: CustomerPayment) => {
    if (!row.invoice_id) return;
    if (row.description?.startsWith("QP")) {
      openQuickPay(row.invoice_id);
    } else {
      openInvoice(row.invoice_id);
    }
  };

  const table = useReactTable({
    data: filteredData,
    columns,
    state: { sorting, columnFilters, globalFilter },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: (row, _colId, filterValue) => {
      const search = filterValue.toLowerCase();
      return (
        row.original.customer_name?.toLowerCase().includes(search) ||
        row.original.payment_id?.toLowerCase().includes(search) ||
        row.original.description?.toLowerCase().includes(search) ||
        row.original.applied_to?.toLowerCase().includes(search)
      );
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 10 } },
  });

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          searchValue={globalFilter}
          setSearchValue={setGlobalFilter}
        />
        <FilterDropdown
          label="Status"
          options={statusOptions}
          selected={statusFilter}
          onChange={toggleStatus}
        />
        {typeOptions.length > 0 && (
          <FilterDropdown
            label="Payment Type"
            options={typeOptions}
            selected={typeFilter}
            onChange={toggleType}
          />
        )}
        <div className="ml-auto">
          <TableColumnsManager table={table} />
        </div>
      </div>

      <MainTable
        table={table}
        isLoading={isLoading}
        onRowClick={handleRowClick}
      />
      <TablePagination table={table} />
    </div>
  );
};
